// オブジェクト単体／複数のドラッグ移動ハンドラ。
//
// 振る舞い:
// - pointerdown:
//   - Ctrl / Meta 押下時は選択集合をトグル（既選択なら外す、未選択なら追加）。
//     ドラッグは行わず、複数選択の構築のみ行う。
//   - Alt 押下時は「複製ドラッグ」（FigJam 風）。移動元は動かさず、マウス追従するゴーストを
//     表示し、ドロップ時に最終位置へ複製を作成する。
//   - 既に複数選択されているオブジェクトを通常クリックした場合は、その複数選択を維持して
//     グループドラッグを開始する。
//   - それ以外は単一選択へ置換し、通常のドラッグを開始する。
//   stage 側へのイベント伝播は止めて、空白部のパン開始と競合しないようにする。
//   付箋/図形/テキスト配置ツール選択中 (place-*) は既存オブジェクト上でも新規配置を許すため、
//   pointerdown を完全にスルーして stage 側に処理を委ねる。
// - pointermove: ドラッグ中はオブジェクト要素（複数選択時は全要素）の left/top を直接書き換えて
//   プレビューする。doc store / 共同編集サーバへは送らない。
//   Shift 押下中は移動量の大きい軸へ吸着し、x 軸または y 軸のみを動かす（直線移動）。
//   他オブジェクトと端／中心が揃うときは、グリッド吸着 ON/OFF に関わらずスナップし補助線を表示する。
//   複製ドラッグ中は移動元を動かさず、ゴーストのみを追従させる。
// - pointerup: ドラッグ距離が閾値以上なら、各オブジェクトの最終位置を transformObject op として送信。
//   複製ドラッグ時は createObjects で複製を作成し、複製を選択状態にする。
//   閾値未満（=ただのクリック）なら何もしない。
// - pointercancel: 確定せずに破棄する。pointercancel はブラウザ / OS が操作を中断したとき
//   （タッチのジェスチャ割り込み、2 本指ピンチの開始、着信通知など）にだけ発生し、利用者が
//   指やボタンを離した合図ではないため、確定すると意図しない位置で移動 / 複製が確定してしまう。
//
// 編集中（contentEditable のテキスト入力）はドラッグを開始しない（pointerdown を見送る）。

import { useEffect, useRef, type RefObject } from "react";
import { customAlphabet } from "nanoid";
import { useShowToast } from "../notice/use-show-toast";
import {
  collectObjectsCarriedByMove,
  collectSectionContainedObjectIds,
  type CanvasObject,
} from "@refinement-canvas/domain";
import type { ViewState } from "../../../lib/canvas-coordinates";
import { SNAP_THRESHOLD_SCREEN_PX, type AlignmentBox } from "../../../lib/canvas-alignment";
import { resolveDragPositions, type DragFrame } from "../../../lib/canvas-drag-resolve";
import { cloneObjectAsDraft } from "../../../lib/canvas-object-clone";
import { collectAlignmentStatics } from "./collect-alignment-statics";
import { isTransientAttachmentObject } from "../attachment-placeholder";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { isObjectPlacementTool, useCanvasStore } from "../store/canvas-store";
import { useAlignmentGuideStore } from "../store/canvas-alignment-store";
import { useConnectorDragStore, type LiveFrame } from "../store/connector-drag-store";
import {
  useCommentAnchorPreviewStore,
  type CommentAnchorPreview,
} from "../store/comment-anchor-preview-store";
import type { CanvasObjectDraft, CanvasOpDispatcher } from "../use-canvas-op-dispatcher";

interface Options {
  readonly objectId: string;
  readonly elementRef: RefObject<HTMLElement | null>;
  readonly dispatcher: CanvasOpDispatcher;
  readonly initialX: number;
  readonly initialY: number;
  readonly initialW: number;
  readonly initialH: number;
  readonly initialRotation?: number | undefined;
  /** 編集中はドラッグを抑止する（テキスト編集中など）。 */
  readonly disabled?: boolean | undefined;
}

// クリックとドラッグを分ける移動量のしきい値（px）。この距離以下の移動は「動いていない」として
// ドラッグを開始しない。リンクカードのダブルクリック遷移（link-card-object.tsx）も同じ値・同じ比較で
// 「ドラッグが成立した操作では遷移しない」を判定するため export する（カード側に別の定数を持たせると
// 両者のしきい値がずれ、ドラッグ成立と遷移が同時に起きる移動量が生まれる）。
export const CLICK_THRESHOLD_PX = 3;
// 他参加者向けゴースト中継の最小送信間隔（ms）。約 20Hz に間引いて通信量を抑える。
const TRANSIENT_THROTTLE_MS = 50;
// 複製（Alt+ドラッグ）で先に id を確定するためのジェネレータ（dispatcher と同じ文字種・長さ）。
// コネクタの端点 id を複製先へ貼り替えるため、createObjects へ渡す前に id を決める必要がある。
const generateDuplicateId = customAlphabet(
  "23456789abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ",
  16
);
// 複製ドラッグのゴーストに付与する z-index（32bit 範囲内で十分大きい値。他オブジェクトより前面）。
const GHOST_Z_INDEX = 2147483000;

// グループドラッグ対象のオブジェクトごとの初期状態と DOM 参照。
// element はビューポートカリングでアンマウント済みの対象では null になる。
// その場合はドラッグ中のライブプレビュー（left/top の直接書き換え・複製ゴースト）だけを
// 省略し、pointerup での確定計算（base 座標ベースで DOM 非依存）には通常どおり参加させる。
// これにより、セクションの内容物が画面外にあっても移動から脱落しない。
interface GroupTarget {
  readonly id: string;
  readonly element: HTMLElement | null;
  readonly baseLeft: number;
  readonly baseTop: number;
  readonly baseWidth: number;
  readonly baseHeight: number;
}

interface DragState {
  startClientX: number;
  startClientY: number;
  baseLeft: number;
  baseTop: number;
  baseWidth: number;
  baseHeight: number;
  baseTransform: string;
  moved: boolean;
  /**
   * 最後の pointermove プレビューで適用したステージ座標差分（Shift 軸固定適用後・clamp 前）。
   * pointerup ではこの値をそのまま確定に使い、「見た目（最後のプレビュー）」と「確定値」を完全一致させる。
   * event.shiftKey を pointerup で再評価すると、リリース直前に Shift を離した場合や、
   * 最後の move と up で座標が僅かに変わって軸の主従が反転した場合に確定値だけずれるため、
   * キー状態ではなく適用済み差分そのものを保持する。
   */
  lastPreviewRawDx: number;
  lastPreviewRawDy: number;
  /** グループドラッグ対象（自分以外）。単一選択時は空配列（セクションは内容物が注入され得る）。 */
  groupTargets: readonly GroupTarget[];
  /**
   * 追従対象へ注入した comment-thread の id。プレビュー用の一時 store へ書く対象、および
   * 他参加者向け transient 中継から除外する対象を、毎フレーム doc store を引かずに決めるために持つ。
   */
  carriedCommentIds: readonly string[];
  /**
   * ドラッグ中に選択ボックス（リサイズ / 回転ハンドルの枠）を追従させるか。
   * 選択ボックスは単一選択時のみ存在するため「複数選択でないこと」で判定する。
   * （セクション単一選択のドラッグは、内容物の注入で groupTargets が非空でも選択は単一のため追従する。）
   */
  previewSelectionBox: boolean;
  /** 他参加者向けゴースト中継を間引くための直近送信時刻（ms）。 */
  lastTransientAt: number;
  /** Alt 押下で開始した複製ドラッグかどうか。 */
  duplicate: boolean;
  /**
   * 複製ドラッグ中に表示する追従ゴースト要素（[自分, ...groupTargets] の順）。move 時に遅延生成する。
   * DOM 要素が無い対象（カリング済み）の位置には null を置き、resolved.positions とのインデックス
   * 対応を保つ（ゴースト表示は省略するが、ドロップ時の複製作成には参加する）。
   */
  ghosts: (HTMLElement | null)[];
  /** 複製ドラッグ開始前の elementRef の cursor。終了時に復元する。 */
  prevCursor: string;
  /**
   * 整列スナップ対象（他オブジェクトの矩形）。初回移動時に一度だけ収集してドラッグ中は固定する。
   * これにより毎フレームの全オブジェクト走査を避け、プレビューと確定で同じ対象を用いて
   * 「見た目」と「確定値」を一致させる。
   */
  staticBoxes: readonly AlignmentBox[];
  /**
   * 整列スナップ（スマートガイド）を有効にするか。移動集合に回転オブジェクトが含まれる場合は、
   * データモデルの x/y/w/h（非回転枠）と見た目の端がずれて誤った補助線が出るため無効化する。
   */
  alignEnabled: boolean;
}

export function useObjectDrag(options: Options): void {
  const {
    objectId,
    elementRef,
    dispatcher,
    initialX,
    initialY,
    initialW,
    initialH,
    initialRotation,
    disabled,
  } = options;
  const showToast = useShowToast();
  // 参照専用（埋め込み）ではオブジェクトの移動 / 複製を無効化する（disabled と同等に扱う）。
  const readOnly = useCanvasStore((s) => s.readOnly);

  // initialX / initialY が effect の依存に含まれるため、他者編集や自分の op.commit
  // エコーで props が更新されるたびに effect が再走する。dragState を effect の
  // closure ローカルに置くと、再走時にドラッグが「中断」されてしまうため、
  // 親 hook 全体で永続する ref に格納しておく。
  const dragStateRef = useRef<DragState | null>(null);
  // initialX/Y/W/H も ref 化しておき、最新の prop 値を listener から参照できるようにする。
  // これにより effect の依存から initialX/Y/W/H を外して再走を抑制できる。
  const baseRef = useRef({
    x: initialX,
    y: initialY,
    w: initialW,
    h: initialH,
    rotation: initialRotation ?? 0,
  });
  baseRef.current = {
    x: initialX,
    y: initialY,
    w: initialW,
    h: initialH,
    rotation: initialRotation ?? 0,
  };

  useEffect(() => {
    // 進行中の複製ゴーストや補助線を確実に片付ける後始末。pointerup を経由せずに
    // ドラッグが中断された場合（アンマウント / disabled への切替）に呼ぶ。
    const teardownActiveDrag = (): void => {
      const ds = dragStateRef.current;
      if (!ds) return;
      dragStateRef.current = null;
      for (const ghost of ds.ghosts) ghost?.remove();
      useAlignmentGuideStore.getState().clearGuides();
      // 直書きした left/top プレビューをストア値へ巻き戻す（複製ドラッグは移動元を動かさない
      // ためスキップ）。中断後に本体 DOM だけプレビュー位置のまま残留させない。
      if (!ds.duplicate) {
        const docObjects = useCanvasDocStore.getState().objects;
        const selfEl = elementRef.current;
        const selfObj = docObjects[objectId];
        if (selfEl && selfObj) {
          selfEl.style.left = `${selfObj.x}px`;
          selfEl.style.top = `${selfObj.y}px`;
        }
        // 選択ボックス（リサイズ / 回転ハンドルの枠）へ直書きしたプレビューも戻す。React は
        // 仮想スタイルが不変な限り書き戻さないため、ここで戻さないと本体だけ元位置へ復帰し、
        // 枠とつまみだけが中断位置に取り残される。
        if (ds.previewSelectionBox && selfObj) {
          updateSelectionBoxPreview({ x: selfObj.x, y: selfObj.y });
        }
        for (const target of ds.groupTargets) {
          const obj = docObjects[target.id];
          if (target.element && obj) {
            target.element.style.left = `${obj.x}px`;
            target.element.style.top = `${obj.y}px`;
          }
        }
      }
      // コネクタ追従用のライブ枠も破棄する。残留するとコネクタが未確定枠を参照し続けるうえ、
      // 選択オーバーレイの自己修復（ライブ枠が在る間はスキップ）が恒久的に働かなくなる。
      useConnectorDragStore
        .getState()
        .clearFrames([objectId, ...ds.groupTargets.map((target) => target.id)]);
      useCommentAnchorPreviewStore.getState().clearAnchors(ds.carriedCommentIds);
      // 複製ドラッグで "copy" に変えたカーソルを復元する（要素がまだ生きていれば）。
      if (ds.duplicate) {
        const selfEl = elementRef.current;
        if (selfEl) selfEl.style.cursor = ds.prevCursor;
      }
    };

    // 編集中（disabled）や参照専用（readOnly）へ切り替わった場合は、進行中ドラッグを中断して
    // 後始末する（listener を張り直さないため、放置するとゴースト / 補助線が残るのを防ぐ）。
    if (disabled || readOnly) {
      teardownActiveDrag();
      return undefined;
    }
    const el = elementRef.current;
    if (!el) {
      teardownActiveDrag();
      return undefined;
    }

    const onPointerDown = (event: PointerEvent): void => {
      // 編集対象がコンテナ自体ではない（contentEditable などへの transit）の場合はスキップ。
      const target = event.target as HTMLElement | null;
      if (target && target !== el && target.isContentEditable) return;
      // 自動ハイパーリンク化された URL 上の pointerdown は、選択・ドラッグ・PointerCapture を
      // 一切開始しない。capture がクリックを奪ってリンク遷移を潰すのを防ぎ、クリックの扱いは
      // リンク側の onClick（確定後は単一クリックで開く）へ委ねる。
      if (target && target.closest?.('[data-canvas-link="true"]')) return;
      // 本文中の表の列幅つまみの上では、選択・ドラッグ・PointerCapture を一切開始しない。
      // capture を奪うと、つまみ側の pointermove / pointerup が届かず列幅を確定できない。
      if (target && target.closest?.('[data-canvas-table-resize="true"]')) return;
      // task checkbox の素のクリックは切り替えへ委ねる。修飾キー付きは従来の選択・複製操作を優先する。
      const onTaskCheckbox = target?.closest?.('[data-canvas-task-checkbox="true"]');
      const hasModifier = event.ctrlKey || event.metaKey || event.shiftKey || event.altKey;
      if (onTaskCheckbox && !hasModifier) return;
      // 主ボタン以外（右クリック等）はドラッグしない
      if (event.button !== 0) return;

      // 付箋/図形/テキスト/スタンプ/コメント配置ツール選択中は、既存オブジェクトの上でも新規配置を許す。
      // 既存オブジェクトの選択もドラッグも開始せず、イベントを stage 側へバブリングさせる。
      const currentTool = useCanvasStore.getState().tool;
      if (isObjectPlacementTool(currentTool)) {
        return;
      }

      // ロックされたオブジェクトは選択のみ許可しドラッグはしない。
      const docObject = useCanvasDocStore.getState().objects[objectId];
      if (docObject?.locked) {
        useCanvasStore.getState().setSelectedObjectIds([objectId]);
        event.stopPropagation();
        return;
      }

      const additive = event.ctrlKey || event.metaKey;
      const currentSelected = useCanvasStore.getState().selectedObjectIds;

      if (additive) {
        // Ctrl/Meta + クリック: 選択集合をトグル。ドラッグはせず、選択操作のみ。
        const set = new Set(currentSelected);
        if (set.has(objectId)) {
          set.delete(objectId);
        } else {
          set.add(objectId);
        }
        useCanvasStore.getState().setSelectedObjectIds(Array.from(set));
        event.stopPropagation();
        return;
      }

      // Alt 押下時は複製ドラッグとして開始する（移動元は据え置き、ドロップ時に複製を作る）。
      const duplicate = event.altKey;

      // 既に複数選択されている対象を通常クリックした場合は選択を維持しグループドラッグへ。
      const isAlreadyMultiSelected =
        currentSelected.length > 1 && currentSelected.includes(objectId);
      if (!isAlreadyMultiSelected) {
        // 単一選択へ置換
        useCanvasStore.getState().setSelectedObjectIds([objectId]);
      }

      // 移動集合に回転オブジェクトが含まれるか。含まれる場合は整列スナップを無効化する
      // （データモデルの非回転枠 x/y/w/h と見た目の端がずれて誤った補助線が出るため）。
      let hasRotatedMoving = Boolean(baseRef.current.rotation);

      // グループドラッグ対象を収集（自分自身は groupTargets には含めず baseLeft/baseTop で別管理）。
      const docObjects = useCanvasDocStore.getState().objects;
      const groupTargets: GroupTarget[] = [];
      if (isAlreadyMultiSelected) {
        for (const id of currentSelected) {
          if (id === objectId) continue;
          const obj = docObjects[id];
          // ロック中オブジェクトはグループドラッグの追従対象から除外する。
          // 選択集合自体は保ったままで、移動だけが起きないようにする。
          if (!obj || obj.locked) continue;
          // コネクタ（接続矢印）は端点の図形に追従するため、グループドラッグでは直接動かさない
          // （端点の図形が動けば自動で再ルーティングされる）。
          if (obj.type === "connector") continue;
          if (obj.rotation) hasRotatedMoving = true;
          groupTargets.push({
            id,
            // カリング済み・未描画で DOM が無い選択図形も、確定 op の対象には残す。
            element: document.querySelector<HTMLElement>(`[data-testid="canvas-object-${id}"]`),
            baseLeft: obj.x,
            baseTop: obj.y,
            baseWidth: obj.w,
            baseHeight: obj.h,
          });
        }
      }

      // オブジェクトを動かすときは、その外接矩形に一部でも（正の面積で）重なり、かつ前面（上に載っている）
      // オブジェクトも一緒に動かす。完全に収まっていなくてもよく、少しはみ出したスタンプも追従する。
      // セクションも同じ規則で、重なる前面のオブジェクトを追従させる。ただし別のセクションは、完全に含まれる
      // 入れ子のものだけを描画順に関わらず追従させる（判定は collectObjectsCarriedByMove）。
      // 親子関係は永続化せず、ドラッグ開始時にこの幾何計算で 1 回だけ決定する。ドラッグ中に重なった
      // オブジェクトは加えない（README §7 / 設計文書 §3-6 のセクション挙動を全オブジェクトへ一般化）。
      // 内容物がビューポート外でカリング済み（DOM 無し）でも element: null の GroupTarget として
      // 参加させ、確定計算から脱落させない。
      const movingIds = new Set<string>([objectId, ...groupTargets.map((t) => t.id)]);
      // 追従判定の起点（mover）は、選択集合そのものではなく「実際に移動するオブジェクト」から決める。
      // ロック中のオブジェクトは上のループで groupTargets（=movingIds）から除外されるため、ロック済み
      // オブジェクトと外部オブジェクトを複数選択して外部をドラッグしても、ロックされた本体は動かないのに
      // 上に載ったオブジェクトだけが動く、という契約破りを防ぐ（ロック中は移動しない）。
      // 起点は追従対象を加える前に確定させ、追従と判定したオブジェクトを起点に加えない。これにより
      // A に B、B に C だけが重なる配置で A を動かしても、C まで連鎖して動くことはない。
      const movers = Array.from(movingIds)
        .map((id) => docObjects[id])
        .filter((obj): obj is CanvasObject => obj !== undefined);
      // 追従対象の走査に使う全オブジェクト配列は、mover が存在するときだけ構築する。
      const allObjects = movers.length > 0 ? Object.values(docObjects) : [];
      const carriedCommentIds: string[] = [];
      for (const mover of movers) {
        // Alt+ドラッグ複製では、セクションのみ内容物ごと複製する既存挙動を維持し、セクション以外の
        // 「上に載ったオブジェクト」は複製に巻き込まない。本要望は移動時の追従のみで、複製の一般化は含まない
        // （セクション以外を複製したときに上の付箋・スタンプまで増えるのを防ぐ）。移動（非複製）では従来どおり追従する。
        if (duplicate && mover.type !== "section") continue;
        // 複製で一緒に複製する内容物は、従来どおりセクションへの完全包含で決める
        // （collectSectionContainedObjectIds）。移動の「前面に重なる」判定を複製へ使うと、セクションから
        // 少しはみ出した付箋まで複製されて増えてしまうため、移動と複製で判定関数を明示的に分ける。
        const carriedIds =
          duplicate && mover.type === "section"
            ? collectSectionContainedObjectIds(allObjects, mover)
            : collectObjectsCarriedByMove(allObjects, mover);
        for (const carriedId of carriedIds) {
          if (movingIds.has(carriedId)) continue;
          const obj = docObjects[carriedId];
          if (!obj) continue;
          // 複製ドラッグではコメントスレッドを運ばない（メッセージごと複製され、会話が二重化するため）。
          // 移動（非複製）では従来の内容物と同じく追従させる。
          if (duplicate && obj.type === "comment-thread") continue;
          movingIds.add(carriedId);
          const isComment = obj.type === "comment-thread";
          if (isComment) carriedCommentIds.push(carriedId);
          if (obj.rotation) hasRotatedMoving = true;
          groupTargets.push({
            id: carriedId,
            element: document.querySelector<HTMLElement>(
              `[data-testid="canvas-object-${carriedId}"]`
            ),
            baseLeft: obj.x,
            baseTop: obj.y,
            // コメントピンは面を持たないアンカー点として扱い、幅高さは 0 で渡す。保存されている
            // 名目値（w/h = 1）を使うと移動集合の外接矩形が最大 1px 広がり、整列スナップや
            // グリッド吸着の結果がコメントの有無で変わる。確定 op は x/y のみで保存値は変えない。
            baseWidth: isComment ? 0 : obj.w,
            baseHeight: isComment ? 0 : obj.h,
          });
        }
      }

      dragStateRef.current = {
        startClientX: event.clientX,
        startClientY: event.clientY,
        baseLeft: baseRef.current.x,
        baseTop: baseRef.current.y,
        baseWidth: baseRef.current.w,
        baseHeight: baseRef.current.h,
        baseTransform: el.style.transform,
        moved: false,
        lastPreviewRawDx: 0,
        lastPreviewRawDy: 0,
        groupTargets,
        carriedCommentIds,
        previewSelectionBox: !isAlreadyMultiSelected,
        lastTransientAt: 0,
        duplicate,
        ghosts: [],
        prevCursor: el.style.cursor,
        staticBoxes: [],
        alignEnabled: !hasRotatedMoving,
      };
      // ネイティブ DnD やテキスト選択が割り込むと pointer capture を失い、
      // lostpointercapture 経由でドラッグが途中確定するため、ドラッグ開始時だけ抑止する。
      // preventDefault で通常のフォーカス移動も止まるため、別オブジェクトの編集中要素は
      // 明示的に blur し、クリックで編集を抜ける従来動作を維持する。
      if (document.activeElement instanceof HTMLElement && document.activeElement !== el) {
        document.activeElement.blur();
      }
      event.preventDefault();
      // jsdom などポインタキャプチャ未実装の環境でも落ちないよう存在チェックする。
      el.setPointerCapture?.(event.pointerId);
      // stage 側のパン開始へ届かないようにする
      event.stopPropagation();
    };

    const onPointerMove = (event: PointerEvent): void => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      const distance = Math.hypot(
        event.clientX - dragState.startClientX,
        event.clientY - dragState.startClientY
      );
      // 閾値未満の手ブレでは left/top を一切書き換えない（クリックの意図を保護する）。
      if (!dragState.moved && distance <= CLICK_THRESHOLD_PX) return;
      const firstMove = !dragState.moved;
      dragState.moved = true;

      const view = useCanvasStore.getState().view;
      // クライアント座標差分はステージ座標では zoom で割る必要がある
      let rawDx = (event.clientX - dragState.startClientX) / view.zoom;
      let rawDy = (event.clientY - dragState.startClientY) / view.zoom;
      // Shift 押下中: 移動量の大きい軸のみを採用し、もう一方を 0 に固定する（直線移動）。
      if (event.shiftKey) {
        if (Math.abs(rawDx) >= Math.abs(rawDy)) {
          rawDy = 0;
        } else {
          rawDx = 0;
        }
      }
      // 適用済み差分を保持し、pointerup ではこれをそのまま確定に使う（プレビューと確定値の一致）。
      dragState.lastPreviewRawDx = rawDx;
      dragState.lastPreviewRawDy = rawDy;

      // 初回移動時に: 整列対象を一度だけ収集し、複製ゴーストを生成し、カーソルを複製用に切り替える。
      if (firstMove) {
        dragState.staticBoxes = collectStaticBoxes(dragState, objectId, view);
        if (dragState.duplicate) {
          createDuplicateGhosts(el, dragState);
          el.style.cursor = "copy";
        }
      }

      const frames = framesOf(dragState);
      // グリッド吸着 ON のときはプレビュー中も吸着を適用し、ドラッグ中から背面グリッドへ
      // 貼り付く挙動にする（確定時と同じ計算を共有するため、確定値との不一致は生じない）。
      // 整列スナップも適用して見た目を揃える。
      const resolved = resolveDragPositions({
        frames,
        rawDx,
        rawDy,
        staticBoxes: dragState.staticBoxes,
        threshold: SNAP_THRESHOLD_SCREEN_PX / view.zoom,
        gridSnapEnabled: useCanvasStore.getState().gridSnapEnabled,
        applyGrid: true,
        alignEnabled: dragState.alignEnabled,
      });

      if (dragState.duplicate) {
        // 複製ドラッグ: 移動元は動かさず、ゴーストのみ追従させる。
        for (let i = 0; i < dragState.ghosts.length; i++) {
          const pos = resolved.positions[i];
          const ghost = dragState.ghosts[i];
          if (!pos || !ghost) continue;
          ghost.style.left = `${pos.x}px`;
          ghost.style.top = `${pos.y}px`;
        }
      } else {
        // 通常移動: 自分と group 要素の left/top を直接書き換えてプレビューする。
        const selfPos = resolved.positions[0];
        if (selfPos) {
          el.style.left = `${selfPos.x}px`;
          el.style.top = `${selfPos.y}px`;
          // 単一選択時のみ選択ボックス（リサイズ／回転ハンドル）も追従させる
          // （セクションの内容物注入で groupTargets が非空でも、選択が単一なら追従する）。
          if (dragState.previewSelectionBox) {
            updateSelectionBoxPreview({ x: selfPos.x, y: selfPos.y });
          }
        }
        for (let i = 0; i < dragState.groupTargets.length; i++) {
          const pos = resolved.positions[i + 1];
          const targetEl = dragState.groupTargets[i]?.element;
          if (!pos || !targetEl) continue;
          targetEl.style.left = `${pos.x}px`;
          targetEl.style.top = `${pos.y}px`;
        }

        // 追従中のコメントピンは objects レイヤに DOM を持たないため、確定前アンカーを一時 store
        // 経由でコメント層へ渡す（groupTargets[i] は resolved.positions[i + 1] に対応する）。
        if (dragState.carriedCommentIds.length > 0) {
          const anchors: Record<string, CommentAnchorPreview> = {};
          for (let i = 0; i < dragState.groupTargets.length; i++) {
            const target = dragState.groupTargets[i];
            const pos = resolved.positions[i + 1];
            if (!target || !pos) continue;
            if (!dragState.carriedCommentIds.includes(target.id)) continue;
            anchors[target.id] = { x: pos.x, y: pos.y };
          }
          useCommentAnchorPreviewStore.getState().setAnchors(anchors);
        }

        // 他参加者へ移動中ゴーストを中継する（通信量を抑えるため一定間隔で間引く）。
        const now = Date.now();
        if (now - dragState.lastTransientAt >= TRANSIENT_THROTTLE_MS) {
          dragState.lastTransientAt = now;
          const updates = resolved.positions
            .map((pos, i) => {
              const id = i === 0 ? objectId : dragState.groupTargets[i - 1]?.id;
              // コメントスレッドは transient ゴーストの対象外。受信側は保存 w/h の破線枠と名前を
              // 描くため、名目 w/h（1px）のスレッドを中継しても意味のある表示にならない。
              if (!id || dragState.carriedCommentIds.includes(id)) return null;
              return { objectId: id, transform: { x: pos.x, y: pos.y } };
            })
            .filter(
              (u): u is { objectId: string; transform: { x: number; y: number } } => u !== null
            );
          dispatcher.previewTransforms(updates);
        }

        // コネクタ（接続矢印）のリアルタイム追従用に、確定前のライブ枠を一時 store へ反映する。
        // 整列スナップ適用後の最終プレビュー位置（resolved.positions）と各図形の w/h から構成する。
        // 回転済み図形でもアンカーが回転位置を保つよう、現在の回転角も載せる。
        const docObjects = useCanvasDocStore.getState().objects;
        const liveFrames: Record<string, LiveFrame> = {};
        for (let i = 0; i < frames.length; i++) {
          const pos = resolved.positions[i];
          const frame = frames[i];
          if (!pos || !frame) continue;
          const id = i === 0 ? objectId : dragState.groupTargets[i - 1]?.id;
          if (!id || dragState.carriedCommentIds.includes(id)) continue;
          const rotation =
            id === objectId ? baseRef.current.rotation : (docObjects[id]?.rotation ?? 0);
          liveFrames[id] = {
            x: pos.x,
            y: pos.y,
            w: frame.baseWidth,
            h: frame.baseHeight,
            rotation,
          };
        }
        useConnectorDragStore.getState().setFrames(liveFrames);
      }

      // 整列補助線を更新する（overlay が購読して描画する）。
      useAlignmentGuideStore.getState().setGuides(resolved.guides);
    };

    const onPointerUp = (event: PointerEvent): void => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      // 最後の pointermove プレビューで適用した差分をそのまま確定に使う。
      // event 座標から再計算すると、リリース直前の Shift 解除や僅かな座標変化で軸の主従が
      // 反転し、見えていたプレビューと確定値がずれる恐れがあるため、保持済み差分を採用する。
      const rawDx = dragState.lastPreviewRawDx;
      const rawDy = dragState.lastPreviewRawDy;
      const moved = dragState.moved;
      const duplicate = dragState.duplicate;
      // 確定後にコネクタのライブ枠を取り除くための対象 id（自分 + グループ）。
      const involvedIds = [objectId, ...dragState.groupTargets.map((target) => target.id)];
      dragStateRef.current = null;
      if (el.hasPointerCapture?.(event.pointerId)) {
        el.releasePointerCapture?.(event.pointerId);
      }
      // 補助線・ゴースト・カーソルを片付ける。
      useAlignmentGuideStore.getState().clearGuides();
      for (const ghost of dragState.ghosts) ghost?.remove();
      if (duplicate) el.style.cursor = dragState.prevCursor;

      if (!moved) return;

      const view = useCanvasStore.getState().view;
      const { gridSnapEnabled } = useCanvasStore.getState();
      const frames = framesOf(dragState);
      // 整列対象は初回移動時に収集済みのものを再利用し、プレビューと確定の整列結果を一致させる。
      // 確定はグリッド吸着を適用（整列が成立した軸はグリッドより整列を優先する）。
      const resolved = resolveDragPositions({
        frames,
        rawDx,
        rawDy,
        staticBoxes: dragState.staticBoxes,
        threshold: SNAP_THRESHOLD_SCREEN_PX / view.zoom,
        gridSnapEnabled,
        applyGrid: true,
        alignEnabled: dragState.alignEnabled,
      });

      if (duplicate) {
        // 複製ドラッグ: 最終位置へ複製を作成し、複製を選択状態にする。
        const docObjects = useCanvasDocStore.getState().objects;
        // groupTargets はコネクタを含まない（コネクタは端点に追従するため移動対象外）。
        // 複製では、両端が複製集合に含まれるコネクタも端点 id を貼り替えて複製する。
        const sourceIds = [objectId, ...dragState.groupTargets.map((t) => t.id)];
        // 端点図形の旧 id → 複製後の新 id。コネクタの端点貼り替えに使う。
        const idMap = new Map<string, string>();
        const endpointDrafts: CanvasObjectDraft[] = [];
        let excludedCount = 0;
        for (let i = 0; i < sourceIds.length; i++) {
          const id = sourceIds[i];
          const obj = id ? docObjects[id] : undefined;
          const pos = resolved.positions[i];
          if (!obj || !pos) continue;
          // アップロード未完了の画像・動画（blob:/data: src）は複製対象から除外する。
          // copy/paste と同様、解決不能な src を持つ object.create を再配信しないため。
          if (isTransientAttachmentObject(obj)) {
            excludedCount++;
            continue;
          }
          const offset = { x: pos.x - obj.x, y: pos.y - obj.y };
          const newId = generateDuplicateId();
          idMap.set(obj.id, newId);
          endpointDrafts.push({
            ...cloneObjectAsDraft(obj, offset),
            id: newId,
            x: pos.x,
            y: pos.y,
          });
        }
        // 選択集合内のコネクタのうち、両端が複製対象に含まれるものを端点 id を貼り替えて複製する。
        // サーバは create 時に端点の存在を検証するため、コネクタは端点図形より後に create する
        // （drafts の末尾へ積む）。
        const connectorDrafts: CanvasObjectDraft[] = [];
        for (const id of useCanvasStore.getState().selectedObjectIds) {
          const obj = docObjects[id];
          if (!obj || obj.type !== "connector") continue;
          const fromNew = idMap.get(obj.from.objectId);
          const toNew = idMap.get(obj.to.objectId);
          if (!fromNew || !toNew) continue;
          connectorDrafts.push({
            ...cloneObjectAsDraft(obj, { x: 0, y: 0 }),
            id: generateDuplicateId(),
            from: { ...obj.from, objectId: fromNew },
            to: { ...obj.to, objectId: toNew },
          } as CanvasObjectDraft);
        }
        const drafts = [...endpointDrafts, ...connectorDrafts];
        if (drafts.length > 0) {
          const created = dispatcher.createObjects(drafts);
          if (created.length > 0) {
            useCanvasStore.getState().setSelectedObjectIds(created.map((o) => o.id));
          }
          if (excludedCount > 0) {
            showToast("info", "アップロード完了前の画像・動画は複製対象から除外しました。");
          }
        } else if (excludedCount > 0) {
          showToast(
            "warning",
            "アップロード完了前の画像・動画は複製できません。完了後にもう一度お試しください。"
          );
        }
        return;
      }

      // 通常移動: 自分とグループ全体の最終位置を DOM へ反映し、transform op を送信する。
      const selfPos = resolved.positions[0];
      if (selfPos) {
        el.style.left = `${selfPos.x}px`;
        el.style.top = `${selfPos.y}px`;
        if (dragState.previewSelectionBox) {
          updateSelectionBoxPreview({ x: selfPos.x, y: selfPos.y });
        }
      }
      if (dragState.groupTargets.length === 0) {
        if (selfPos) {
          // 単一選択：従来通り transformObject を 1 回呼ぶ。
          dispatcher.transformObject(objectId, selfPos);
        }
      } else {
        // 複数選択（またはセクションの内容物注入）：自分とグループ全体の transform を
        // 1 つの履歴エントリにまとめることで、Ctrl+Z 1 回で全オブジェクトの相対位置がそろって戻る。
        const updates: {
          readonly objectId: string;
          readonly transform: { x: number; y: number };
        }[] = [];
        if (selfPos) updates.push({ objectId, transform: selfPos });
        for (let i = 0; i < dragState.groupTargets.length; i++) {
          const target = dragState.groupTargets[i];
          const pos = resolved.positions[i + 1];
          if (!target || !pos) continue;
          // カリング済み（DOM 無し）の対象はプレビュー反映を省略し、op 送信だけ行う。
          if (target.element) {
            target.element.style.left = `${pos.x}px`;
            target.element.style.top = `${pos.y}px`;
          }
          updates.push({ objectId: target.id, transform: pos });
        }
        dispatcher.transformObjects(updates);
      }
      // 確定（doc store へ反映）後にライブ枠を破棄する。これにより、移動が無かった場合も含め
      // コネクタは確定済みの doc 値を参照して描画へ収束する（ちらつきを避けるため dispatch の後）。
      useConnectorDragStore.getState().clearFrames(involvedIds);
      useCommentAnchorPreviewStore.getState().clearAnchors(dragState.carriedCommentIds);
    };

    // pointercancel（ブラウザ / OS による中断、2 本指ピンチ開始時の取り消し）では確定せずに破棄する。
    // 破棄の本体（ゴースト除去・補助線消去・直書きプレビューの巻き戻し・ライブ枠とコメント
    // アンカーの破棄・カーソル復元）は teardownActiveDrag が持つため、ここでは pointer capture の
    // 解放だけを足す。状態を先に捨ててから解放し、解放に伴う lostpointercapture が届いても
    // onPointerUp が確定へ進まないようにする（use-pointer-mode と同じ順序）。
    const onPointerCancel = (event: PointerEvent): void => {
      if (!dragStateRef.current) return;
      teardownActiveDrag();
      if (el.hasPointerCapture?.(event.pointerId)) {
        el.releasePointerCapture?.(event.pointerId);
      }
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerCancel);
    // pointerup を経ずにポインタキャプチャを失った場合（タブ切替・OS/ブラウザ都合の中断等）の
    // 受け皿。onPointerUp は最後の pointermove プレビュー差分（lastPreviewRawDx/Dy）で確定する
    // 設計のため、そのまま流用して「見えていた位置」を確定し、直接 DOM プレビューだけが残って
    // 確定 op が送られない乖離（枠線とつまみがサイズ変更・移動へ追従しない実障害の同族）を防ぐ。
    // 通常フローでは pointerup が先に dragStateRef を null にするため二重確定しない。
    el.addEventListener("lostpointercapture", onPointerUp);
    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerCancel);
      el.removeEventListener("lostpointercapture", onPointerUp);
      // アンマウント（el が DOM から切り離された）時のみ、進行中ドラッグのゴースト / 補助線を
      // 後始末する。dep 変更による effect 再走（el は接続されたまま）ではドラッグを継続させたいので
      // 何もしない（dragState は ref で保持され、再走後の listener が引き続き処理する）。
      if (!el.isConnected) teardownActiveDrag();
    };
    // 注意: initialX / initialY / initialW / initialH / initialRotation を依存から外している。最新値は baseRef
    // 経由で listener から参照するため、effect 再走による listener 張り直しは不要。
    // （ドラッグ中に他者編集で props が変動しても自分のドラッグが中断されないようにする。）
  }, [objectId, elementRef, dispatcher, disabled, readOnly, showToast]);
}

/** dragState から [自分, ...groupTargets] の DragFrame 配列を作る。 */
function framesOf(dragState: DragState): readonly DragFrame[] {
  return [dragState, ...dragState.groupTargets];
}

/**
 * 整列対象となる他オブジェクトの矩形を集める。移動対象自身（複製ドラッグでは複製元）は常に除外する。
 * 含めると、複製ドラッグでしきい値内の小移動時に複製元へ吸着し、ゴーストが動かず元位置へ重なった
 * 複製ができてしまうため。複製は既存の他オブジェクトへ整列させる。
 * 収集ロジック自体はリサイズと共有する collectAlignmentStatics に委譲する。
 */
function collectStaticBoxes(
  dragState: DragState,
  objectId: string,
  view: ViewState
): readonly AlignmentBox[] {
  const movingIds = new Set<string>([objectId, ...dragState.groupTargets.map((t) => t.id)]);
  return collectAlignmentStatics(movingIds, view);
}

/**
 * 複製ドラッグ用の追従ゴーストを生成し objects-layer へ追加する。
 * 各要素を cloneNode で複製し、data-testid を除去（選択 / クエリ衝突回避）、
 * 選択 UI（data-canvas-selection-ui）を要素ごと除去（id 重複回避）、
 * pointerEvents 無効・半透明化して「複製プレビュー」であることを示す。
 */
function createDuplicateGhosts(el: HTMLElement, dragState: DragState): void {
  const layer = el.closest('[data-testid="canvas-objects-layer"]');
  if (!layer) return;
  // カリング済み（DOM 無し）の対象は null を保ち、resolved.positions とのインデックス対応を崩さない
  // （ゴースト表示は省略されるが、ドロップ時の複製作成には参加する）。
  const sources: (HTMLElement | null)[] = [el, ...dragState.groupTargets.map((t) => t.element)];
  for (const source of sources) {
    if (!source) {
      dragState.ghosts.push(null);
      continue;
    }
    const ghost = source.cloneNode(true) as HTMLElement;
    ghost.removeAttribute("data-testid");
    ghost.querySelectorAll("[data-testid]").forEach((node) => node.removeAttribute("data-testid"));
    // 選択 UI（ペンストロークの mask 定義と選択輪郭）はゴーストへ持ち込まない。クローンは
    // 同一文書へ挿入されるため、mask の id が重複して url(#...) の参照先が一意に定まらなくなる。
    // mask 属性だけを外すと膨張シルエット全体が選択色で塗られて残るため、要素ごと取り除く。
    ghost.querySelectorAll('[data-canvas-selection-ui="true"]').forEach((node) => node.remove());
    // クローン内の contentEditable は編集対象にならないよう無効化する。
    ghost
      .querySelectorAll("[contenteditable]")
      .forEach((node) => node.setAttribute("contenteditable", "false"));
    // 動画クローンは音が二重再生されないようミュートする。
    ghost.querySelectorAll("video").forEach((video) => {
      (video as HTMLVideoElement).muted = true;
    });
    ghost.setAttribute("aria-hidden", "true");
    ghost.setAttribute("data-canvas-drag-ghost", "true");
    ghost.style.pointerEvents = "none";
    ghost.style.opacity = "0.7";
    ghost.style.zIndex = String(GHOST_Z_INDEX);
    layer.appendChild(ghost);
    dragState.ghosts.push(ghost);
  }
}

function updateSelectionBoxPreview(next: {
  readonly x?: number | undefined;
  readonly y?: number | undefined;
}): void {
  const box = document.querySelector<HTMLElement>('[data-testid="canvas-selection-box"]');
  if (!box) return;
  if (next.x !== undefined) box.style.left = `${next.x}px`;
  if (next.y !== undefined) box.style.top = `${next.y}px`;
}
